import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2, QrCode, CreditCard, Loader2 } from "lucide-react";
import { brl } from "@/lib/format";
import { toast } from "sonner";

export const PaymentMethodDialog = ({ plan, open, onClose, onPix, onCard }) => {
  const yearly = plan?.price_yearly && plan.price_yearly < plan.price * 12 ? plan.price_yearly : 0;
  const saving = plan ? Math.max(0, plan.price * 12 - yearly) : 0;
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="payment-method-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Como deseja pagar o plano {plan?.name}?</DialogTitle>
        <DialogDescription>{brl(plan?.price || 0)} por mês</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 mt-2">
        <button onClick={() => onPix("monthly")} data-testid="pay-method-pix"
          className="flex items-center gap-4 rounded-xl border-2 border-stone-200 p-4 text-left hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
          <QrCode className="h-8 w-8 text-emerald-600 shrink-0" />
          <div>
            <div className="font-bold text-stone-900">PIX mensal — {brl(plan?.price || 0)}</div>
            <div className="text-sm text-stone-500">QR Code instantâneo. Libera 30 dias de acesso. Renove quando quiser.</div>
          </div>
        </button>
        {yearly > 0 && (
          <button onClick={() => onPix("yearly")} data-testid="pay-method-pix-yearly"
            className="relative flex items-center gap-4 rounded-xl border-2 border-emerald-300 bg-emerald-50/40 p-4 text-left hover:border-emerald-600 hover:bg-emerald-50 transition-colors">
            <span className="absolute -top-2.5 right-3 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Economize {brl(saving)}</span>
            <QrCode className="h-8 w-8 text-emerald-700 shrink-0" />
            <div>
              <div className="font-bold text-stone-900">PIX anual — {brl(yearly)}</div>
              <div className="text-sm text-stone-500">Pague uma vez e garanta 365 dias de acesso ({brl(yearly / 12)}/mês).</div>
            </div>
          </button>
        )}
        <button onClick={onCard} data-testid="pay-method-card"
          className="flex items-center gap-4 rounded-xl border-2 border-stone-200 p-4 text-left hover:border-orange-500 hover:bg-orange-50 transition-colors">
          <CreditCard className="h-8 w-8 text-orange-600 shrink-0" />
          <div>
            <div className="font-bold text-stone-900">Cartão de crédito — recorrente</div>
            <div className="text-sm text-stone-500">Assinatura automática mensal via Mercado Pago.</div>
          </div>
        </button>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export const PixCheckoutDialog = ({ pix, open, onClose, onPaid }) => {
  const [status, setStatus] = useState(pix?.status || "pending");

  useEffect(() => {
    if (!open || !pix?.payment_id || status === "approved") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/subscription/pix/${pix.payment_id}`);
        setStatus(r.data.status);
        if (r.data.status === "approved") { clearInterval(t); onPaid(r.data); }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [open, pix?.payment_id, status, onPaid]);

  const copy = async () => {
    await navigator.clipboard.writeText(pix.qr_code);
    toast.success("Código PIX copiado");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pix-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Pague com PIX</DialogTitle>
          <DialogDescription>Escaneie o QR Code ou copie o código no app do seu banco.</DialogDescription>
        </DialogHeader>
        {status === "approved" ? (
          <div className="flex flex-col items-center py-8 gap-3" data-testid="pix-approved">
            <CheckCircle2 className="h-16 w-16 text-emerald-600" />
            <div className="font-display text-xl font-extrabold">Pagamento confirmado!</div>
            <p className="text-sm text-stone-500 text-center">Seu plano foi liberado por {pix?.days || 30} dias.</p>
            <Button onClick={onClose} className="bg-orange-600 hover:bg-orange-700" data-testid="pix-done-btn">Fechar</Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="font-mono-num text-3xl font-extrabold text-emerald-700" data-testid="pix-amount">{brl(pix?.amount || 0)}</div>
            {pix?.qr_code_base64 && (
              <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code PIX"
                className="h-56 w-56 rounded-xl border border-stone-200" data-testid="pix-qr-image" />
            )}
            <div className="w-full">
              <div className="text-xs uppercase text-stone-500 font-semibold mb-1">PIX copia e cola</div>
              <div className="flex gap-2">
                <input readOnly value={pix?.qr_code || ""} data-testid="pix-code-input"
                  className="flex-1 rounded-md border border-stone-200 bg-stone-50 px-2 py-2 text-xs font-mono truncate" />
                <Button variant="outline" size="icon" onClick={copy} data-testid="pix-copy-btn"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="pix-status">
              <Loader2 className="h-4 w-4 animate-spin" /> Aguardando pagamento… a confirmação é automática.
            </div>
            {pix?.expires_at && <div className="text-xs text-stone-400">Válido até {new Date(pix.expires_at).toLocaleString("pt-BR")}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
